import React, { useEffect, useRef, useState } from 'react';
import './styles/LayerSlideNumber.scss';
import type { LayerSwitchDirection } from '../context/LayerSwitchContext';
import { NumberCounter } from './NumberCounterComponent';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';

type SlidePhase = 'idle' | 'exit-up' | 'exit-down' | 'enter-from-below' | 'enter-from-above';

function slideClass(phase: SlidePhase): string {
  return phase === 'idle' ? '' : `layer-slide-${phase}`;
}

interface LayerSlideRoundUnitProps {
  roundUnit?: number;
  /** 丸めなし時ホバーで ~0 を表示（×1 と同型） */
  showZeroWhenIdle?: boolean;
  direction: LayerSwitchDirection;
  switchNonce: number;
  slideEnabled?: boolean;
  className?: string;
  isNegative?: boolean;
  animateFromZero?: boolean;
  duration?: number;
  /** false にすると ~N のカウントアップを即時表示にする（長押し連打中のちらつき防止） */
  countAnimate?: boolean;
  onClick?: (e: React.MouseEvent | React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  tooltipHtml?: string;
}

/** レイヤー切替時に ~N 表示を縦スライド（LayerSlideNumber と同型） */
export const LayerSlideRoundUnit: React.FC<LayerSlideRoundUnitProps> = ({
  roundUnit,
  showZeroWhenIdle = false,
  direction,
  switchNonce,
  slideEnabled = true,
  className = '',
  isNegative = false,
  animateFromZero = false,
  duration = 300,
  countAnimate = true,
  onClick,
  onTouchEnd,
  tooltipHtml,
}) => {
  const hasActiveRound = roundUnit !== undefined && roundUnit > 0;
  const displayNum = hasActiveRound ? roundUnit! : 0;

  const [displayRu, setDisplayRu] = useState(hasActiveRound ? roundUnit : showZeroWhenIdle ? 0 : undefined);
  const [displayNegative, setDisplayNegative] = useState(isNegative);
  const [animPhase, setAnimPhase] = useState<SlidePhase>('idle');
  const [zeroAnimating, setZeroAnimating] = useState(false);
  const committedNonce = useRef(switchNonce);
  const pendingRef = useRef(roundUnit);
  const pendingNegativeRef = useRef(isNegative);
  const prevRoundRef = useRef(roundUnit);
  const zeroAnimTimerRef = useRef<number | null>(null);
  pendingRef.current = roundUnit;
  pendingNegativeRef.current = isNegative;
  const isAnimating = animPhase !== 'idle';
  const slidePending = direction !== 0 && switchNonce === committedNonce.current;
  const slideActive = switchNonce !== committedNonce.current || isAnimating;
  const layerSlideHiding = slideActive || slidePending;
    const countDuration = isAnimating ? 0 : duration;
  const phaseClass = slideActive ? slideClass(animPhase) : '';
  const showZeroIdle = showZeroWhenIdle && !hasActiveRound && !zeroAnimating && !layerSlideHiding;
  /** ~0 はホバー時のみ表示。レイヤー切替中（pending/active）は非マウント */
  const showContent =
    zeroAnimating ||
    (displayRu !== undefined && displayRu > 0) ||
    // 0→1 到達フレームで displayRu(=0) が effect で更新される前に
    // 「!hasActiveRound」条件が外れて 1 フレーム空表示になるのを防ぐ。
    // レイヤースライド中は従来どおり非マウント。
    (hasActiveRound && !layerSlideHiding) ||
    (showZeroWhenIdle && displayRu === 0 && !layerSlideHiding && !hasActiveRound);
  const idleZeroClass = showZeroIdle ? ' round-zero-idle' : '';
  const zeroAnimClass = zeroAnimating ? ' round-zero-animating' : '';

  useEffect(() => {
    return () => {
      if (zeroAnimTimerRef.current !== null) {
        window.clearTimeout(zeroAnimTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const pending = direction !== 0 && switchNonce === committedNonce.current;
    if (!slideEnabled && !pending && switchNonce === committedNonce.current) {
      committedNonce.current = switchNonce;
      return;
    }
    if (switchNonce === committedNonce.current) return;

    if (direction === 0) {
      const ru = pendingRef.current;
      setDisplayRu(ru !== undefined && ru > 0 ? ru : showZeroWhenIdle ? 0 : undefined);
      setDisplayNegative(pendingNegativeRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      // レイヤー切替で確定した値を prevRound に同期し、切替後に
      // 「丸めあり→なし」の zero フェード(残像 ~0)が誤発火するのを防ぐ。
      prevRoundRef.current = ru;
      return;
    }

    const exitPhase: SlidePhase = direction === 1 ? 'exit-up' : 'exit-down';
    const enterPhase: SlidePhase = direction === 1 ? 'enter-from-below' : 'enter-from-above';
    const targetNonce = switchNonce;

    setAnimPhase(exitPhase);

    const tEnter = window.setTimeout(() => {
      const ru = pendingRef.current;
      setDisplayRu(ru !== undefined && ru > 0 ? ru : showZeroWhenIdle ? 0 : undefined);
      setDisplayNegative(pendingNegativeRef.current);
      setAnimPhase(enterPhase);
      // スライドで切替先レイヤーの値が確定したので prevRound を同期する。
      // これをしないと slide 終了直後に zero フェード(残像 ~0)が誤発火し、
      // 「丸めあり→丸めなし」レイヤーへ切替えると一瞬 ~0 がフェードアウト表示される。
      prevRoundRef.current = ru;
    }, LAYER_SLIDE_MS);

    const tIdle = window.setTimeout(() => {
      setAnimPhase('idle');
      committedNonce.current = targetNonce;
    }, LAYER_SLIDE_MS * 2);

    return () => {
      window.clearTimeout(tEnter);
      window.clearTimeout(tIdle);
    };
  }, [switchNonce, direction, slideEnabled, showZeroWhenIdle]);

  useEffect(() => {
    if (slidePending || slideActive) return;

    const prev = prevRoundRef.current;
    const hadRound = prev !== undefined && prev > 0;
    const nowRound = roundUnit !== undefined && roundUnit > 0;

    if (hadRound && !nowRound) {
      if (zeroAnimTimerRef.current !== null) {
        window.clearTimeout(zeroAnimTimerRef.current);
      }
      setZeroAnimating(true);
      setDisplayRu(prev);
      const raf = window.requestAnimationFrame(() => {
        setDisplayRu(0);
      });
      zeroAnimTimerRef.current = window.setTimeout(() => {
        setZeroAnimating(false);
        setDisplayRu(showZeroWhenIdle ? 0 : undefined);
        zeroAnimTimerRef.current = null;
      }, duration);
      prevRoundRef.current = roundUnit;
      return () => {
        window.cancelAnimationFrame(raf);
      };
    }

    prevRoundRef.current = roundUnit;
    if (zeroAnimating) return;
    setDisplayRu(hasActiveRound ? roundUnit : showZeroWhenIdle ? 0 : undefined);
    setDisplayNegative(isNegative);
  }, [
    roundUnit,
    isNegative,
    showZeroWhenIdle,
    hasActiveRound,
    switchNonce,
    slidePending,
    slideActive,
    duration,
  ]);

  if (!showContent) {
    return null;
  }

  const counterValue = displayRu ?? displayNum;
  // 長押し連打中（countAnimate=false）はカウントアップを止めて即時表示にする。
  // NumberCounter は連打を 500ms 窓で検出して即時化するが、~N 表示はマウント直後
  // （0→1 到達時）の 1000ms 間は連打判定が無効になり、tick ごとに 300ms tween が
  // 再スタート→直前確定値へ戻してから再加算するため数値が前後にちらつく。
  const counterDuration = !countAnimate ? 0 : zeroAnimating ? duration : countDuration;

  const wrapperClass = [
    'layer-slide-formatted-value',
    'multiplier-round-unit',
    displayNegative ? 'negative' : 'positive',
    idleZeroClass,
    zeroAnimClass,
    className,
    phaseClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={wrapperClass}
      data-pouch-tooltip-html={tooltipHtml || undefined}
      onClick={onClick}
      onTouchEnd={onTouchEnd}
    >
      {showContent ? (
        <>
          {'~ '}
          <NumberCounter
            value={counterValue}
            animateFromZero={countAnimate && animateFromZero && !isAnimating && !zeroAnimating}
            duration={counterDuration}
            suppressTooltip
          />
        </>
      ) : null}
    </span>
  );
};
